import { dialCodeFor } from './countries';
import type { PhoneLib } from './phone-lib';

/**
 * Convierte lo que escribe (o autocompleta) la persona en un número E.164 para el país elegido.
 *
 * Casos reales que motivan cada regla:
 *  - 24-sep y 6-oct-2026: el autocompletado del móvil pegaba «+61…» o «+1…»; el campo borraba el
 *    «+» y anteponía el país elegido → +6161…, +571…, +11… (números a los que nadie podía escribir).
 *    Ahora el «+» o «00» se respeta y fija el país (ver splitInternational en el componente).
 *  - «0» nacional (Australia 0447…) e indicativo repetido (61417…, 1305…): libphonenumber-js los
 *    resuelve con el país elegido.
 *  - 14-oct-2026: se aceptaron «+57» + 6 dígitos y «+57 2…» (no son números colombianos). La
 *    validación ahora es la del país, no una longitud genérica.
 */

export interface ResolvedWhatsapp {
  /** Valor que viaja al servidor: E.164 si se pudo interpretar, si no `+<indicativo><dígitos>`. */
  value: string;
  /** ISO del país del número (puede diferir del elegido si la librería lo identifica mejor). */
  iso: string;
  /** El número es válido para su país según libphonenumber-js (null si la librería no cargó). */
  valid: boolean | null;
}

export function resolveWhatsapp(nationalText: string, iso: string, lib: PhoneLib | null): ResolvedWhatsapp {
  const digits = String(nationalText ?? '').replace(/\D/g, '');
  const dial = dialCodeFor(iso);
  if (!digits) return { value: '', iso, valid: false };
  const fallback = `+${dial}${digits}`;
  if (!lib) return { value: fallback, iso, valid: null };
  try {
    const parsed = lib.parsePhoneNumberFromString(digits, iso as Parameters<PhoneLib['parsePhoneNumberFromString']>[1]);
    if (parsed && String(parsed.countryCallingCode) === dial) {
      return { value: parsed.number, iso: parsed.country ?? iso, valid: parsed.isValid() };
    }
  } catch {
    // Entrada incompleta: se conserva el valor sin interpretar.
  }
  return { value: fallback, iso, valid: lib.isValidPhoneNumber(fallback) };
}

/**
 * Validación del formulario: con la librería cargada, número válido para su país. Sin ella
 * (red lenta, chunk bloqueado), forma E.164 con 10–15 dígitos: cubre los números habituales
 * y rechaza el «+57» + 6 dígitos del 14-oct-2026.
 */
export function isAcceptableWhatsapp(value: string, lib: PhoneLib | null): boolean {
  const cleaned = String(value ?? '').trim().replace(/[\s\-().]/g, '');
  if (!/^\+[1-9]\d{3,14}$/.test(cleaned)) return false;
  if (!lib) return /^\+[1-9]\d{9,14}$/.test(cleaned);
  try {
    return lib.isValidPhoneNumber(cleaned);
  } catch {
    return false;
  }
}
